/**
 * How a list draws its rows: a table, a grid of cards, or tiles.
 *
 * Presentation only. Filter, search, sort and pagination mean the same thing
 * in every layout, so switching never changes which rows are on screen — only
 * how much of each one is visible at a glance.
 *
 * `warehouse` is Stock Levels' own (E1, #441) and the one exception to "which
 * rows": it regroups the same stock a variant to a row, a column per
 * warehouse. The same filter still keeps the same stock; what changes is that
 * a variant held in three warehouses is one row, not three.
 */
export type ListLayout = "table" | "grid" | "tile" | "warehouse";

export const LIST_LAYOUTS: readonly ListLayout[] = [
  "table",
  "grid",
  "tile",
  "warehouse",
];

function isLayout(value: unknown): value is ListLayout {
  return (
    typeof value === "string" && (LIST_LAYOUTS as string[]).includes(value)
  );
}

const storageKey = (scope: string) => `estelle.layout.${scope}`;

/**
 * The layout this browser last chose for a list, if it is still one the list
 * offers.
 *
 * Wrapped because `localStorage` throws outright in a browser set to block
 * site data, and a preference is not worth a list that fails to render. Per
 * browser rather than per person until there is a users table to hold it.
 */
export function readLayoutPreference(
  scope: string,
  offered: readonly ListLayout[],
): ListLayout | undefined {
  try {
    const stored = localStorage.getItem(storageKey(scope));
    return isLayout(stored) && offered.includes(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

export function writeLayoutPreference(scope: string, layout: ListLayout): void {
  try {
    localStorage.setItem(storageKey(scope), layout);
  } catch {
    // Unwritable storage costs the preference, not the switch.
  }
}

/**
 * Resolve the layout to render.
 *
 * The URL wins, because a link is explicit; then this browser's preference;
 * then the table. A layout the section does not offer — a stale link, a
 * preference saved before a module dropped one — falls through rather than
 * rendering a layout with no screen behind it.
 */
export function resolveLayout(
  fromUrl: string | undefined,
  scope: string,
  offered: readonly ListLayout[],
): ListLayout {
  if (isLayout(fromUrl) && offered.includes(fromUrl)) return fromUrl;
  return readLayoutPreference(scope, offered) ?? "table";
}
